import { Fragment, Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { OverlayPanel } from 'primereact/overlaypanel'
import { RadioButton } from 'primereact/radiobutton'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

// local imports
import CampusService, { ICampusServiceExists } from 'service/CampusService'
import { IEmployeeSummary } from 'types/Employee'
import AuthUtil from 'utils/AuthUtil'
import useDidMountEffect from 'utils/useDidMountEffect'
import { DayNames } from './DayNames'

/* Assets */
import doctor from 'assets/icons/doctor.svg'
import gym from 'assets/icons/gym.svg'
import hair from 'assets/icons/hairdresser.svg'
import lactation from 'assets/icons/baby.svg'
import meeting from 'assets/icons/meeting-room.svg'
import park from 'assets/icons/park-area.svg'
import poolVehicle from 'assets/icons/pool-vehicle.svg'

export default function CalendarHeader(props: {
  employee: React.RefObject<IEmployeeSummary | null>
  showLoader: boolean
  showSquadsEvent: boolean
  canGoBack: boolean
  canGoForward: boolean
  selectedEmployee: IEmployeeSummary
  modalMonth: moment.Moment
  setModalMonth: Dispatch<SetStateAction<moment.Moment>>
  month: moment.Moment
  setMonth: Dispatch<SetStateAction<moment.Moment>>
  filterSquadsEvents: (arg0: any) => any
  isDateValid: (date: moment.Moment) => boolean
  cleanDaysState: (value: any) => (dispatch: any) => void
}) {
  /* ########### Props Destructuring  ########### */
  const {
    employee,
    showSquadsEvent,
    selectedEmployee,
    showLoader,
    canGoBack,
    canGoForward,
    isDateValid,
    modalMonth,
    setModalMonth,
    filterSquadsEvents,
    month,
    setMonth,
    cleanDaysState,
  } = props || {}
  /* ########### Props Destructuring  ########### */

  const { t } = useTranslation()
  const op = useRef<any>(null)
  const opCampus = useRef<any>(null)
  let [selectedMonth, setSelectedMonth] = useState(month.clone())
  const [squadEventType, setSquadEventType] = useState('all')

  /* Has Campus Services Exists */
  const [hasCampusPlan, setHasCampusPlan] = useState<boolean>(false)
  const [campusVisible, setCampusVisible] = useState<ICampusServiceExists | undefined>()
  const hasHealth = AuthUtil.hasHealth(employee?.current?.exemptionSpecList!)
  const hasGym = AuthUtil.hasGym(employee?.current?.exemptionSpecList!)
  const hasCoiffeur = AuthUtil.hasCoiffeur(employee?.current?.exemptionSpecList!)
  const hasLactation = AuthUtil.hasLactation(employee?.current?.exemptionSpecList!)
  const hasParkArea = AuthUtil.hasParkArea(employee?.current?.exemptionSpecList!)
  const hasMeeting = AuthUtil.hasMeeting(employee?.current?.exemptionSpecList!)
  const hasPoolVehicle = AuthUtil.hasPoolVehicle(employee?.current?.exemptionSpecList!)

  useEffect(() => {
    let isMounted = true
    if (employee.current) setHasCampusPlan(AuthUtil.hasCampus(employee?.current?.exemptionSpecList!))
    CampusService.getCampusServiceExists()
      .then((r) => {
        if (isMounted) setCampusVisible(r.data)
      })
      .catch((err) => console.log(err))
    
    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    isAnyExist,
    isCoiffeurServiceExist,
    isGymServiceExist,
    isHealthServiceExist,
    isLactationServiceExist,
    isParkAreaServiceExist,
    isMeetingServiceExist,
    isPoolVehicleServiceExist,
  } = campusVisible || {}

  const onCampusServices = useMemo(
    () => [
      hasHealth && isHealthServiceExist
        ? { key: 'health-service', icon: doctor, text: 'health-service' }
        : { key: 'health-service' },
      hasGym && isGymServiceExist ? { key: 'gym', icon: gym, text: 'gym' } : { key: 'gym' },
      hasCoiffeur && isCoiffeurServiceExist ? { key: 'coiffeur', icon: hair, text: 'coiffeur' } : { key: 'coiffeur' },
      hasLactation && isLactationServiceExist
        ? { key: 'lactation-room', icon: lactation, text: 'lactation-room' }
        : { key: 'lactation' },
      hasParkArea && isParkAreaServiceExist ? { key: 'park', icon: park, text: 'parking-lot' } : { key: 'park' },
      hasMeeting && isMeetingServiceExist
        ? { key: 'meeting', icon: meeting, text: 'meeting-room' }
        : { key: 'meeting' },
      hasPoolVehicle && isPoolVehicleServiceExist
        ? { key: 'pool-vehicle', icon: poolVehicle, text: 'pool-vehicle' }
        : { key: 'pool-vehicle' },
    ],
    [
      hasHealth,
      isHealthServiceExist,
      hasGym,
      isGymServiceExist,
      hasCoiffeur,
      isCoiffeurServiceExist,
      hasLactation,
      isLactationServiceExist,
      hasParkArea,
      isParkAreaServiceExist,
      hasMeeting,
      isMeetingServiceExist,
      hasPoolVehicle,
      isPoolVehicleServiceExist,
    ]
  )
  /* Has Campus Services Exists */

  useDidMountEffect(() => {
    filterSquadsEvents(squadEventType)
  }, [squadEventType])

  const handleMonthClick = (e: any) => {
    op?.current?.toggle(e)
  }

  const previous = () => {
    cleanDaysState(true)
    setMonth(month.clone().subtract(1, 'months'))
  }

  const next = () => {
    cleanDaysState(true)
    setMonth(month.clone().add(1, 'months'))
  }

  const handleModalMonthClick = (ind: number) => {
    cleanDaysState(true)
    selectedMonth = modalMonth.clone().month(ind)

    setSelectedMonth(selectedMonth)
    setMonth(selectedMonth.clone())
  }

  const checkClicked = (ind: number) => {
    const selectedDate = selectedMonth.clone().format('YYYYMM')
    const modalDate = modalMonth.clone().month(ind).format('YYYYMM')
    if (selectedDate === modalDate) return ' of-calendar-modal-selected'
    return ''
  }

  const previousMonthOfModal = () => {
    setModalMonth(modalMonth.clone().subtract(1, 'year'))
  }

  const nextMonthOfModal = () => {
    setModalMonth(modalMonth.clone().add(1, 'year'))
  }

  const modalTd = (ind: number) => {
    let isActive =
      isDateValid(modalMonth.clone().month(ind).startOf('month')) ||
      isDateValid(modalMonth.clone().month(ind).endOf('month'))
    return (
      <td
        key={ind}
        className={`of-calendar-modal-item ${!isActive ? 'disabled' : null}` + checkClicked(ind)}
        onClick={(e) => handleModalMonthClick(ind)}>
        {modalMonth.clone().month(ind).format('MMMM')}
      </td>
    )
  }

  const modalTr = (start: number, end: number) => {
    let tdArray: any = []
    for (let i = start; i < end; i++) {
      tdArray.push(modalTd(i))
    }
    return <tr>{tdArray}</tr>
  }

  const handleHoverCampusServices = (e: any) => opCampus?.current?.toggle(e)

  const shouldRenderComponent =
    !!campusVisible &&
    isAnyExist &&
    (hasCampusPlan || isParkAreaServiceExist || isMeetingServiceExist || isPoolVehicleServiceExist)

  return (
    <>
      <header>
        <div className="row of-plan-header">
          {employee !== null && employee.current !== null && employee.current.id !== selectedEmployee?.id ? (
            <div className="d-flex of-calendar-name ps-0">{selectedEmployee?.fullName}</div>
          ) : null}

          {/* Month Slider - Event Types */}
          <div className="d-flex justify-content-between flex-column flex-xxl-row  align-items-center ps-0 ">
            {/* Month Slider */}
            <div className="d-flex gap-2 align-items-center p-2 ps-0 of-month-display ">
              <div onClick={(e) => handleMonthClick(e)} className="month-label">
                {month.format('MMMM YYYY')}
              </div>

              <div className="d-flex of-arrows justify-content-between">
                <i
                  id="monthPrev"
                  className={`arrow pi pi-angle-left ${!canGoBack ? 'disabled' : ''}`}
                  onClick={previous}
                />
                <i
                  id="monthNext"
                  className={`arrow pi pi-angle-right ${!canGoForward ? 'disabled' : ''}`}
                  onClick={next}
                />
              </div>
            </div>

            {/* Event Types & Filter */}
            {/* TODO: check squadsEvents */}
            {showSquadsEvent ? (
              <div className="d-flex p-2 pe-0 of-plan-label-row">
                <div className="of-plan-label">
                  <RadioButton
                    inputId="all"
                    name="all"
                    value="all"
                    onChange={(e) => setSquadEventType(e.value)}
                    checked={squadEventType === 'all'}
                  />
                  <span id="lblOfficeId" className="dot micro-dot dot-all" />
                  <label htmlFor="all" className="squads-legend">
                    {t('calendar.all-notifications')}
                  </label>
                </div>
                <div className="of-plan-label">
                  <RadioButton
                    inputId="office"
                    name="office"
                    value="office"
                    onChange={(e) => setSquadEventType(e.value)}
                    checked={squadEventType === 'office'}
                  />
                  <span id="lblOfficeId" className="dot micro-dot dot-office" />
                  <label htmlFor="office" className="squads-legend">
                    {t('calendar.work-from-office')}
                  </label>
                </div>
                <div className="of-plan-label">
                  <RadioButton
                    inputId="commonOffice"
                    name="commonOffice"
                    value="commonOffice"
                    onChange={(e) => setSquadEventType(e.value)}
                    checked={squadEventType === 'commonOffice'}
                  />
                  <span id="lblCommonOfficeId" className="dot micro-dot dot-common-office"></span>
                  <label htmlFor="commonOffice" className="squads-legend">
                    {t('calendar.work-from-shared-office')}
                  </label>
                </div>
                <div className="of-plan-label">
                  <RadioButton
                    inputId="remote"
                    name="remote"
                    value="remote"
                    onChange={(e) => setSquadEventType(e.value)}
                    checked={squadEventType === 'remote'}
                  />
                  <span id="lblRemoteId" className="dot micro-dot dot-home"></span>
                  <label htmlFor="remote" className="squads-legend">
                    {t('tabs.remote-work')}
                  </label>
                </div>
              </div>
            ) : (
              <div className="d-flex p-2 pe-0 of-plan-label-row">
                <div className="of-plan-label">
                  <span id="lblLeaveReportId" className="dot dot-leave-report" />
                  <label htmlFor="lblLeaveReportId">{t('calendar.leave-report')}</label>
                </div>
                <div className="of-plan-label">
                  <span id="lblOfficeId" className="dot dot-office" />
                  <label htmlFor="lblOfficeId">{t('calendar.work-from-my-office')}</label>
                </div>
                <div className="of-plan-label">
                  <span id="lblCommonOfficeId" className="dot dot-common-office"></span>
                  <label htmlFor="lblCommonOfficeId">{t('calendar.work-from-shared-office')}</label>
                </div>
                <div className="of-plan-label">
                  <span id="lblRemoteId" className="dot dot-home"></span>
                  <label htmlFor="lblRemoteId">{t('tabs.remote-work')}</label>
                </div>
                <div className="of-plan-label">
                  <span id="lblRemoteId" className="dot dot-holiday"></span>
                  <label htmlFor="lblRemoteId">{t('calendar.holiday')}</label>
                </div>

                {/* Campus Services */}
                {shouldRenderComponent ? (
                  <div
                    className="of-plan-label"
                    onMouseLeave={(e) => opCampus?.current.hide()}
                    onMouseEnter={(e) => handleHoverCampusServices(e)}>
                    <span id="lblCampusId" className="dot dot-campus-service"></span>
                    <label htmlFor="lblCampusId">{t('calendar.campus-service')}</label>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
        {showLoader ? null : <DayNames />}
      </header>

      {/* On Campus Overlay */}
      {shouldRenderComponent ? (
        <OverlayPanel ref={opCampus} style={{ borderRadius: '10px' }}>
          <div className="d-flex flex-column gap-2">
            {onCampusServices.map((item) => (
              <Fragment key={item.key}>
                {!!item?.icon && (
                  <div key={item.key} className="d-flex gap-2 align-items-center px-1">
                    <img className="mr-2" src={item.icon} alt={item.key} />
                    <span>{t(`campus-services.${item.text}`)}</span>
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        </OverlayPanel>
      ) : null}

      {/* MonthPicker Overlay */}
      <OverlayPanel ref={op} className="of-calendar-overlay">
        <div className=" d-flex row">
          <div className="d-flex p-2">
            <span className="month-label">{modalMonth.format('YYYY')}</span>
            <i className="arrow pi pi-angle-left" onClick={previousMonthOfModal} />
            <i className="arrow pi pi-angle-right of-text-left" onClick={nextMonthOfModal} />
            <div className="d-flex flex-row-reverse w-100">
              <i className="arrow pi pi-times of-times-btn" onClick={(e) => op?.current?.hide()} />
            </div>
          </div>
        </div>
        <div className="row">
          <table className="table  table-borderless of-calendar-modal-table">
            <tbody>
              {modalTr(0, 4)}
              {modalTr(4, 8)}
              {modalTr(8, 12)}
            </tbody>
          </table>
        </div>
      </OverlayPanel>
    </>
  )
}
